import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Search, AlertOctagon, ArrowDownToLine, ArrowUpFromLine, Trash2, Hammer,
  Repeat, ShieldAlert, ArrowRight, X, Mail, Send
} from 'lucide-react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';

const kindMeta = (ttype) => {
  if (ttype === 'Receive' || ttype === 'Return') return { Icon: ArrowDownToLine, color: 'text-emerald-600' };
  if (ttype === 'Scrap') return { Icon: Trash2, color: 'text-red-600' };
  if (ttype === 'Consume') return { Icon: ArrowUpFromLine, color: 'text-slate-600' };
  if (ttype === 'BuildComplete') return { Icon: Hammer, color: 'text-purple-600' };
  if (ttype === 'Transfer') return { Icon: Repeat, color: 'text-blue-600' };
  return { Icon: ArrowDownToLine, color: 'text-slate-500' };
};

const BatchLotRecallPage = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [lotInput, setLotInput] = useState(searchParams.get('lot') || '');
  const [trace, setTrace] = useState(null);
  const [recalls, setRecalls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallForm, setRecallForm] = useState({ reason: '', notes: '' });
  const [submittingRecall, setSubmittingRecall] = useState(false);
  // iter67 — Recall by SKU (covers every lot of a given SKU)
  const [skuRecallOpen, setSkuRecallOpen] = useState(false);
  const [skuRecallForm, setSkuRecallForm] = useState({ item_sku: '', reason: '', notes: '' });
  const [submittingSkuRecall, setSubmittingSkuRecall] = useState(false);

  const [forwardTrace, setForwardTrace] = useState(null);  // iter32

  // iter48: email recall letter compose state
  const [emailingUnit, setEmailingUnit] = useState(null);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [loadingDraft, setLoadingDraft] = useState(false);

  const openEmailRecallDialog = async (unit) => {
    setEmailingUnit(unit);
    setEmailRecipient(unit.shipped_to || '');
    setEmailSubject('');
    setEmailBody('');
    setLoadingDraft(true);
    try {
      const activeRecallIdForLot = (recalls.find(r => r.status === 'active' && r.lot === (trace?.lot || forwardTrace?.lot)) || {}).id;
      const params = new URLSearchParams();
      if (activeRecallIdForLot) params.set('recall_id', activeRecallIdForLot);
      const res = await api().get(`/finished-units/${unit.id}/recall-notification?${params.toString()}`);
      const d = res.data;
      setEmailSubject(`URGENT — Product Recall Notification (${d.reference_number})`);
      const compsHtml = (d.implicated_components || []).map(c =>
        `<li>${c.component_sku} — ${c.item_name || ''} (qty ${c.quantity_consumed || ''})${c.vendor_name ? `, vendor ${c.vendor_name}` : ''}</li>`
      ).join('');
      const html = [
        `<h2 style="color:#b91c1c;margin:0 0 8px;">Product Recall Notification</h2>`,
        `<p style="font-family:Menlo,monospace;color:#b91c1c;font-size:13px;margin:0 0 16px;">${d.reference_number}</p>`,
        `<p>Dear <strong>${d.consignee.name || 'Customer'}</strong>,</p>`,
        `<p>This letter is to formally notify you that <strong>Lot ${d.recall.lot}</strong> shipped on BOL <strong>${d.unit.bol_number || '—'}</strong> (tracking: ${d.unit.shipped_tracking || '—'}) is being recalled.</p>`,
        `<p><strong>Reason:</strong> ${d.recall.reason || ''}</p>`,
        `<p><strong>Affected unit serial:</strong> ${d.unit.serial_number || d.unit.id}</p>`,
        compsHtml ? `<p><strong>Implicated components:</strong></p><ul>${compsHtml}</ul>` : '',
        `<p>${d.recommended_action}</p>`,
        `<p>Sincerely,<br/>${d.shipper.name}</p>`,
      ].filter(Boolean).join('');
      setEmailBody(html);
    } catch (e) {
      toast.error('Failed to load draft');
    } finally {
      setLoadingDraft(false);
    }
  };

  const sendRecallEmail = async () => {
    if (!emailingUnit) return;
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    setSendingEmail(true);
    try {
      const activeRecallIdForLot = (recalls.find(r => r.status === 'active' && r.lot === (trace?.lot || forwardTrace?.lot)) || {}).id;
      const res = await api().post(`/finished-units/${emailingUnit.id}/recall-notification/email`, {
        to: emailRecipient.split(/[,;\s]+/).filter(e => e.includes('@')),
        recall_id: activeRecallIdForLot,
        // note: backend renders the recall_letter template by default; if user
        // edited the body, send raw via fallback fields
        custom_subject: emailSubject,
        custom_html: emailBody,
      });
      const status = res.data?.status;
      if (status === 'sent') toast.success('Recall letter emailed');
      else if (status === 'skipped') toast.error(`Skipped: ${res.data?.result?.reason || 'unknown'}`);
      else toast.error(`Resend error: ${res.data?.result?.error || 'unknown'}`);
      setEmailingUnit(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send recall email');
    } finally {
      setSendingEmail(false);
    }
  };

  const fetchRecalls = async () => {
    try {
      const { data } = await api().get('/batch-lots/recalls');
      setRecalls(data || []);
    } catch {
      toast.error('Failed to load recalls');
    }
  };

  const runTrace = async (lotValue) => {
    if (!lotValue.trim()) { setTrace(null); setForwardTrace(null); return; }
    setLoading(true);
    try {
      const [traceRes, fwdRes] = await Promise.all([
        api().get(`/batch-lots/trace/${encodeURIComponent(lotValue.trim())}`),
        api().get(`/batch-lots/${encodeURIComponent(lotValue.trim())}/forward-trace`).catch(() => ({ data: null })),
      ]);
      setTrace(traceRes.data);
      setForwardTrace(fwdRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Trace failed');
      setTrace(null);
      setForwardTrace(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecalls();
    const initial = searchParams.get('lot');
    if (initial) runTrace(initial);
    // eslint-disable-next-line
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    if (lotInput.trim()) {
      setSearchParams({ lot: lotInput.trim() }, { replace: true });
      runTrace(lotInput);
    }
  };

  const issueRecall = async (e) => {
    e.preventDefault();
    if (!recallForm.reason.trim()) { toast.error('Reason is required'); return; }
    setSubmittingRecall(true);
    try {
      await api().post('/batch-lots/recall', {
        lot: trace.lot,
        reason: recallForm.reason.trim(),
        notes: recallForm.notes.trim() || null,
      });
      toast.success(`Recall issued for ${trace.lot}. Purchaser notified.`);
      setRecallOpen(false);
      setRecallForm({ reason: '', notes: '' });
      runTrace(trace.lot);
      fetchRecalls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue recall');
    } finally {
      setSubmittingRecall(false);
    }
  };

  const closeRecall = async (recall) => {
    if (!window.confirm(`Close recall for lot ${recall.lot}?`)) return;
    try {
      await api().put(`/batch-lots/recalls/${recall.id}/close`);
      toast.success('Recall closed');
      fetchRecalls();
      if (trace?.lot === recall.lot) runTrace(recall.lot);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close recall');
    }
  };

  // iter67 — issue a SKU-wide recall (covers every lot of a given SKU)
  const issueSkuRecall = async (e) => {
    e.preventDefault();
    const sku = (skuRecallForm.item_sku || '').trim();
    if (!sku) { toast.error('SKU is required'); return; }
    if (!skuRecallForm.reason.trim()) { toast.error('Reason is required'); return; }
    if (!window.confirm(`Recall EVERY lot of SKU "${sku}"? This will flag every transaction and finished unit using this part. Continue?`)) return;
    setSubmittingSkuRecall(true);
    try {
      await api().post('/batch-lots/recall', {
        item_sku: sku,
        reason: skuRecallForm.reason.trim(),
        notes: skuRecallForm.notes.trim() || null,
      });
      toast.success(`SKU-wide recall issued for ${sku}.`);
      setSkuRecallOpen(false);
      setSkuRecallForm({ item_sku: '', reason: '', notes: '' });
      fetchRecalls();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue SKU recall');
    } finally {
      setSubmittingSkuRecall(false);
    }
  };

  // iter33: open a printable recall-notification letter in a new window
  const openRecallLetter = async (unit) => {
    try {
      const activeRecallIdForLot = (recalls.find(r => r.status === 'active' && r.lot === (trace?.lot || forwardTrace?.lot)) || {}).id;
      const params = new URLSearchParams();
      if (activeRecallIdForLot) params.set('recall_id', activeRecallIdForLot);
      const res = await api().get(`/finished-units/${unit.id}/recall-notification?${params.toString()}`);
      const d = res.data;
      const html = `<!doctype html><html><head><title>Recall Notification ${d.reference_number}</title>
        <style>
          @page { size: Letter; margin: 0.75in; }
          body { font-family: Georgia, serif; color: #111; line-height: 1.5; }
          h1 { font-size: 22px; margin: 0 0 4px 0; }
          .ref { color: #b91c1c; font-family: Menlo, monospace; font-size: 13px; margin-top: 4px; }
          .banner { background: #fee2e2; color: #991b1b; padding: 8px 12px; margin: 18px 0; font-weight: bold; letter-spacing: 0.08em; text-transform: uppercase; border-left: 4px solid #991b1b; }
          .row { display: flex; gap: 24px; margin: 22px 0; }
          .col { flex: 1; }
          .label { font-size: 10px; color: #666; text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 3px; }
          table { width: 100%; border-collapse: collapse; margin: 12px 0; }
          td, th { padding: 6px 8px; border-bottom: 1px solid #ddd; text-align: left; font-size: 12px; }
          th { background: #f4f4f5; }
          .action { background: #fffbeb; border: 1px solid #f59e0b; padding: 12px; margin: 18px 0; font-size: 13px; }
          .sign { margin-top: 38px; display: flex; gap: 60px; }
          .sign > div { flex: 1; }
          .line { border-top: 1px solid #555; margin-top: 36px; padding-top: 4px; font-size: 11px; color: #666; }
          .footer { margin-top: 40px; font-size: 10px; color: #999; }
        </style></head><body>
        <div style="display:flex; justify-content:space-between; align-items:flex-start;">
          <div><!--LH--></div>
          <div style="text-align:right; font-size:11px;">
            <div><strong>Reference</strong> · <span class="ref">${d.reference_number}</span></div>
            <div>Generated ${new Date(d.generated_at).toLocaleString()}</div>
            <div>By ${d.generated_by || ''}</div>
          </div>
        </div>
        <div class="banner">Product Recall Notification</div>
        <div class="row">
          <div class="col">
            <div class="label">To</div>
            <div><strong>${d.consignee.name || '—'}</strong></div>
            <div style="white-space:pre-line;">${d.consignee.address || ''}</div>
          </div>
          <div class="col">
            <div class="label">Shipment</div>
            <div>BOL #: <strong>${d.unit.bol_number || '—'}</strong></div>
            <div>Tracking: ${d.unit.shipped_tracking || '—'}</div>
            <div>Shipped: ${d.unit.shipped_at ? new Date(d.unit.shipped_at).toLocaleDateString() : '—'}</div>
          </div>
        </div>
        <p>Dear ${d.consignee.name || 'Customer'},</p>
        <p>We are issuing a recall on the unit listed below. Our records show it contains a component from a lot that has been identified as non-conforming.</p>
        <table>
          <tr><th style="width:38%">Serial Number</th><td>${d.unit.serial_number}</td></tr>
          <tr><th>Product</th><td>${d.unit.product_name || '—'}</td></tr>
          <tr><th>Build Date</th><td>${d.unit.build_date ? new Date(d.unit.build_date).toLocaleDateString() : '—'}</td></tr>
          <tr><th>Affected Lot</th><td class="ref">${d.recall.lot}</td></tr>
          <tr><th>Reason</th><td>${d.recall.reason || ''}</td></tr>
          <tr><th>Recall Issued</th><td>${d.recall.issued_at ? new Date(d.recall.issued_at).toLocaleString() : '—'} by ${d.recall.issued_by || ''}</td></tr>
        </table>
        ${(d.implicated_components || []).length > 0 ? `
        <div class="label" style="margin-top:18px;">Implicated Components</div>
        <table>
          <tr><th>SKU</th><th>Name</th><th style="text-align:right">Qty</th><th>Vendor</th></tr>
          ${d.implicated_components.map(c => `<tr><td class="ref">${c.component_sku}</td><td>${c.item_name || ''}</td><td style="text-align:right">${c.quantity_consumed ?? ''}</td><td>${c.vendor_name || ''}</td></tr>`).join('')}
        </table>` : ''}
        <div class="action">
          <strong>Recommended Action:</strong> ${d.recommended_action}
        </div>
        <p>We apologise for the inconvenience. If you have already deployed, sold, or installed this unit, please contact us immediately so we can coordinate an appropriate remediation.</p>
        <div class="sign">
          <div><div class="line">Customer signature / received date</div></div>
          <div><div class="line">Customer name (printed)</div></div>
        </div>
        <div class="footer">This recall notification was generated by ${escape(d?.shipper?.name || 'our system')}. Retain for your records.</div>
        <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
        </body></html>`;
      const win = await openLetterheadWindow(html, { features: 'width=900,height=1100' });
      if (!win) { toast.error('Pop-ups blocked. Allow pop-ups to print the recall letter.'); return; }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate recall letter');
    }
  };

  const activeRecalls = recalls.filter(r => r.status === 'active');

  // iter42 Phase E.4 — Recall Search by product / assembly / item / sku / lot
  const [searchQuery, setSearchQuery] = useState('');
  const [searchKind, setSearchKind] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const runRecallSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams({ q: searchQuery.trim() });
      if (searchKind) params.append('kind', searchKind);
      const r = await api().get(`/recalls/search?${params.toString()}`);
      setSearchResults(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="batch-lot-recall-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Batch / Lot Recall</h1>
          <p className="text-slate-500 text-sm">Trace a lot across receipts, consumption and disposals — and issue a recall.</p>
        </div>
        <Button
          onClick={() => setSkuRecallOpen(true)}
          variant="outline"
          className="border-red-300 text-red-700 hover:bg-red-50 rounded-sm"
          data-testid="recall-by-sku-btn"
        >
          <ShieldAlert className="w-4 h-4 mr-2" /> Recall Entire SKU
        </Button>
      </div>

      {/* iter42 Phase E.4 — Recall Search by product / assembly / item / sku / lot */}
      <Card className="industrial-card border-amber-200" data-testid="recall-search-card">
        <CardHeader className="border-b border-amber-100 pb-3">
          <h2 className="text-sm font-semibold flex items-center gap-2"><Search className="w-4 h-4 text-amber-600" /> Recall Search</h2>
          <p className="text-xs text-slate-500">Search across recalls by product type, BOM/assembly name, item SKU/name, or lot number.</p>
        </CardHeader>
        <CardContent className="p-4">
          <form onSubmit={runRecallSearch} className="flex gap-2" data-testid="recall-search-form">
            <select value={searchKind} onChange={(e) => setSearchKind(e.target.value)} className="border border-slate-200 rounded-sm h-9 px-3 text-sm" data-testid="recall-search-kind">
              <option value="">Any</option>
              <option value="product">Product</option>
              <option value="assembly">Assembly / BOM</option>
              <option value="item">Item / SKU</option>
              <option value="lot">Lot</option>
            </select>
            <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="e.g., HVAC, BOM-RV-001, CAP-22UF, LOT-20260205" className="rounded-sm flex-1 mono" data-testid="recall-search-input" />
            <Button type="submit" disabled={searching} className="bg-amber-600 hover:bg-amber-700 rounded-sm" data-testid="recall-search-btn">{searching ? 'Searching…' : 'Search'}</Button>
            {searchResults && <Button type="button" variant="outline" onClick={() => { setSearchResults(null); setSearchQuery(''); }} className="rounded-sm">Clear</Button>}
          </form>
          {searchResults && (
            <div className="mt-4 space-y-2" data-testid="recall-search-results">
              <div className="flex items-center gap-3 text-xs text-slate-600">
                <span className="font-semibold">{searchResults.totals.recalls}</span> recall(s) found
                {searchResults.totals.active > 0 && <span className="text-red-600">· {searchResults.totals.active} active</span>}
                {searchResults.matched_skus?.length > 0 && <span className="text-slate-500">· matched SKUs: {searchResults.matched_skus.slice(0, 5).join(', ')}{searchResults.matched_skus.length > 5 ? '…' : ''}</span>}
              </div>
              {(searchResults.notes || []).map((n, i) => <p key={i} className="text-xs text-slate-500 italic">{n}</p>)}
              {searchResults.recalls.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400">No matching recalls.</div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lot</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Affected SKUs</TableHead>
                      <TableHead>Issued</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.recalls.map(r => (
                      <TableRow key={r.id} className={r.status === 'active' ? 'bg-red-50' : ''} data-testid={`recall-search-row-${r.id}`}>
                        <TableCell className="mono text-sm">{r.lot}</TableCell>
                        <TableCell><span className={`text-xs px-2 py-0.5 rounded-sm ${r.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{r.status}</span></TableCell>
                        <TableCell className="text-sm">{r.reason || '—'}</TableCell>
                        <TableCell className="text-xs text-slate-600 max-w-[300px] truncate">{(r.affected_skus || []).slice(0, 5).join(', ')}{(r.affected_skus || []).length > 5 ? '…' : ''}</TableCell>
                        <TableCell className="text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                        <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => { setLotInput(r.lot); runTrace(r.lot); }} data-testid={`recall-trace-${r.id}`}>Trace Lot</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search */}
      <Card className="industrial-card">
        <CardContent className="p-5">
          <form onSubmit={onSearch} className="flex gap-2" data-testid="lot-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Enter batch / lot number — e.g. LOT-20260205"
                value={lotInput}
                onChange={(e) => setLotInput(e.target.value)}
                className="pl-9 rounded-sm mono"
                data-testid="lot-search-input"
              />
            </div>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="lot-search-btn">
              Trace
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Trace results */}
      {loading && <div className="flex justify-center py-8"><div className="spinner" /></div>}

      {trace && !loading && (
        <Card className="industrial-card" data-testid="lot-trace-results">
          <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-slate-700">Lot</h2>
              <span className="mono text-base font-bold text-slate-900">{trace.lot}</span>
              {trace.active_recall && (
                <span
                  className="text-xs uppercase tracking-wider px-2 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm flex items-center gap-1"
                  data-testid="lot-active-recall-badge"
                >
                  <ShieldAlert className="w-3 h-3" /> Active Recall
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!trace.active_recall && trace.transactions.length > 0 && (
                <Button
                  onClick={() => setRecallOpen(true)}
                  className="bg-red-600 hover:bg-red-700 rounded-sm"
                  data-testid="issue-recall-btn"
                >
                  <ShieldAlert className="w-4 h-4 mr-2" /> Issue Recall
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-5 space-y-5">
            {trace.transactions.length === 0 ? (
              <div className="text-sm text-slate-500 text-center py-6" data-testid="lot-trace-empty">
                No transactions recorded for this lot.
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Events</div><div className="text-xl font-bold mono">{trace.totals.events}</div></div>
                  <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3"><div className="text-xs uppercase text-emerald-700">Received</div><div className="text-xl font-bold mono text-emerald-700" data-testid="trace-total-received">{trace.totals.received}</div></div>
                  <div className="border border-slate-200 rounded-sm p-3"><div className="text-xs uppercase text-slate-500">Consumed</div><div className="text-xl font-bold mono">{trace.totals.consumed}</div></div>
                  <div className="border border-red-200 bg-red-50 rounded-sm p-3"><div className="text-xs uppercase text-red-700">Disposed</div><div className="text-xl font-bold mono text-red-700" data-testid="trace-total-disposed">{trace.totals.disposed}</div></div>
                </div>

                {/* Affected items */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Affected Items</div>
                  <div className="flex flex-wrap gap-2" data-testid="lot-affected-items">
                    {trace.affected_items.map(it => (
                      <button
                        key={it.item_sku}
                        onClick={() => it.id && navigate(`/items/view/${it.id}`)}
                        className="border border-slate-200 hover:border-orange-400 rounded-sm px-3 py-2 text-left text-sm group"
                        data-testid={`affected-item-${it.item_sku}`}
                      >
                        <div className="mono font-medium text-slate-800 group-hover:text-orange-600">{it.item_sku}</div>
                        <div className="text-xs text-slate-500">{it.item_name || '—'}</div>
                        {it.current_quantity != null && (
                          <div className="text-xs text-slate-400 mono">On hand: {it.current_quantity} {it.base_uom}</div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Transactions */}
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Transactions</div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trace.transactions.map(t => {
                        const { Icon, color } = kindMeta(t.transaction_type);
                        return (
                          <TableRow key={t.id} data-testid={`lot-txn-${t.id}`}>
                            <TableCell><Icon className={`w-4 h-4 ${color}`} /></TableCell>
                            <TableCell>{t.transaction_type}</TableCell>
                            <TableCell>
                              <button
                                onClick={() => t.item_id && navigate(`/items/view/${t.item_id}`)}
                                className="text-orange-600 hover:underline"
                              >
                                <span className="mono">{t.item_sku}</span>
                                {t.item_name && <span className="text-slate-500 text-xs ml-1">— {t.item_name}</span>}
                              </button>
                            </TableCell>
                            <TableCell className="text-right mono">{t.quantity} {t.uom}</TableCell>
                            <TableCell className="text-sm text-slate-600">{t.user_name}</TableCell>
                            <TableCell className="text-xs text-slate-500">{t.timestamp ? new Date(t.timestamp).toLocaleString() : ''}</TableCell>
                            <TableCell className="text-xs text-slate-500 max-w-xs truncate">{t.notes || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* iter32 — Forward Trace: units consuming this lot */}
      {forwardTrace && (forwardTrace.totals?.affected_units || 0) > 0 && (
        <Card className="industrial-card border-orange-200" data-testid="forward-trace-card">
          <CardHeader className="border-b border-orange-100 pb-3">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-orange-600" />
              <h2 className="text-sm font-semibold text-slate-700">Forward Trace — Units Consuming <span className="mono text-orange-600">{forwardTrace.lot}</span></h2>
              <span className="ml-auto text-xs mono text-slate-500" data-testid="forward-trace-count">
                {forwardTrace.totals.affected_units} unit(s) · {forwardTrace.totals.direct_units} direct
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Every finished-unit serial that consumed this lot (direct or inherited via a part-assembly parent). Shipped units are surfaced too for field-recall.</p>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="data-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Serial #</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Lifecycle</TableHead>
                  <TableHead>Built</TableHead>
                  <TableHead>Built By</TableHead>
                  <TableHead>Relation</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(forwardTrace.units || []).map(u => (
                  <TableRow key={u.id} data-testid={`forward-trace-row-${u.id}`}>
                    <TableCell className="mono font-medium">{u.serial_number}</TableCell>
                    <TableCell className="text-xs">{u.unit_kind === 'final_product' ? 'Final Product' : 'Part Assembly'}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] uppercase px-2 py-0.5 rounded ${u.lifecycle_state === 'shipped' ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                        {u.lifecycle_state}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{u.build_date ? new Date(u.build_date).toLocaleDateString() : '—'}</TableCell>
                    <TableCell className="text-xs text-slate-600">{u.built_by_name || '—'}</TableCell>
                    <TableCell className="text-xs">
                      {u.relation === 'direct'
                        ? <span className="text-slate-700">Direct</span>
                        : <span className="text-slate-500 italic">Inherited (via PA child)</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/unit-genealogy?unit=${u.serial_number}`)} data-testid={`forward-trace-open-${u.id}`}>
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-orange-600"
                        onClick={() => openRecallLetter(u)}
                        data-testid={`forward-trace-letter-${u.id}`}
                        title="Print customer recall notification letter"
                      >
                        Recall Letter
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-emerald-700"
                        onClick={() => openEmailRecallDialog(u)}
                        data-testid={`forward-trace-email-${u.id}`}
                        title="Compose & email recall letter"
                      >
                        <Mail className="w-3.5 h-3.5 mr-1" /> Email
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Active recalls */}
      <Card className="industrial-card">
        <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <AlertOctagon className="w-4 h-4 text-red-600" /> Active Recalls
          </h2>
          <span className="text-xs text-slate-500 mono" data-testid="active-recall-count">{activeRecalls.length}</span>
        </CardHeader>
        <CardContent className="p-0">
          {activeRecalls.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500" data-testid="active-recalls-empty">No active recalls.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activeRecalls.map(r => (
                <li key={r.id} className="p-4 flex flex-wrap items-start gap-3" data-testid={`active-recall-${r.id}`}>
                  <ShieldAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 mono">{r.lot}</span>
                      <span className="text-xs text-slate-500">
                        {r.created_at ? new Date(r.created_at).toLocaleString() : ''} • by {r.created_by_name}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 mt-0.5">{r.reason}</div>
                    {r.notes && <div className="text-sm text-slate-500 mt-1 whitespace-pre-wrap">{r.notes}</div>}
                    <div className="text-xs text-slate-400 mt-1">
                      {r.affected_skus?.length || 0} SKU(s) • {r.transaction_count} transaction(s)
                      {typeof r.affected_unit_count === 'number' && (
                        <span className="ml-2 text-red-600" data-testid={`recall-unit-count-${r.id}`}>
                          · {r.affected_unit_count} finished unit{r.affected_unit_count === 1 ? '' : 's'} flagged
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setLotInput(r.lot); setSearchParams({ lot: r.lot }, { replace: true }); runTrace(r.lot); }}
                      className="rounded-sm"
                      data-testid={`view-recall-${r.id}`}
                    >
                      <ArrowRight className="w-3 h-3 mr-1" /> View
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => closeRecall(r)}
                      className="rounded-sm text-slate-600"
                      data-testid={`close-recall-${r.id}`}
                    >
                      <X className="w-3 h-3 mr-1" /> Close
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Issue recall dialog */}
      <Dialog open={recallOpen} onOpenChange={setRecallOpen}>
        <DialogContent className="sm:max-w-md" data-testid="issue-recall-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" /> Issue Recall — {trace?.lot}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={issueRecall} className="space-y-4">
            <div className="text-sm text-slate-600">
              Issuing a recall will notify the Purchasing team and flag this lot across all affected items.
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input
                value={recallForm.reason}
                onChange={(e) => setRecallForm({ ...recallForm, reason: e.target.value })}
                placeholder="e.g. Vendor announced defective batch"
                required
                className="rounded-sm"
                data-testid="recall-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={recallForm.notes}
                onChange={(e) => setRecallForm({ ...recallForm, notes: e.target.value })}
                placeholder="Quarantine instructions, vendor RMA, etc."
                className="rounded-sm"
                data-testid="recall-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRecallOpen(false)} className="rounded-sm">Cancel</Button>
              <Button
                type="submit"
                disabled={submittingRecall}
                className="bg-red-600 hover:bg-red-700 rounded-sm"
                data-testid="recall-confirm-btn"
              >
                <ShieldAlert className="w-4 h-4 mr-2" /> {submittingRecall ? 'Issuing…' : 'Confirm Recall'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter67 — Recall Entire SKU dialog */}
      <Dialog open={skuRecallOpen} onOpenChange={setSkuRecallOpen}>
        <DialogContent className="max-w-md" data-testid="recall-by-sku-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" /> Recall Entire SKU
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={issueSkuRecall} className="space-y-4">
            <div className="text-sm bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
              ⚠ This recalls EVERY lot of the chosen SKU — every receipt, build, finished unit, and customer shipment using this part will be flagged. Use only when a vendor announces a part-level defect.
            </div>
            <div className="space-y-2">
              <Label>Item SKU *</Label>
              <Input
                value={skuRecallForm.item_sku}
                onChange={(e) => setSkuRecallForm({ ...skuRecallForm, item_sku: e.target.value.toUpperCase() })}
                placeholder="e.g. CAP-22UF-50V"
                required
                className="rounded-sm mono"
                data-testid="recall-sku-input"
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Input
                value={skuRecallForm.reason}
                onChange={(e) => setSkuRecallForm({ ...skuRecallForm, reason: e.target.value })}
                placeholder="e.g. Vendor-wide capacitor defect notice"
                required
                className="rounded-sm"
                data-testid="recall-sku-reason-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={skuRecallForm.notes}
                onChange={(e) => setSkuRecallForm({ ...skuRecallForm, notes: e.target.value })}
                placeholder="Quarantine instructions, vendor RMA, replacement plan, etc."
                className="rounded-sm"
                data-testid="recall-sku-notes-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSkuRecallOpen(false)} className="rounded-sm">Cancel</Button>
              <Button
                type="submit"
                disabled={submittingSkuRecall}
                className="bg-red-600 hover:bg-red-700 rounded-sm"
                data-testid="recall-sku-confirm-btn"
              >
                <ShieldAlert className="w-4 h-4 mr-2" /> {submittingSkuRecall ? 'Issuing…' : 'Confirm SKU Recall'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* iter48: email recall letter compose dialog */}
      <Dialog open={!!emailingUnit} onOpenChange={(o) => !o && setEmailingUnit(null)}>
        <DialogContent className="max-w-3xl" data-testid="recall-email-dialog">
          <DialogHeader>
            <DialogTitle>Email Recall Letter — {emailingUnit?.serial_number}</DialogTitle>
          </DialogHeader>
          {loadingDraft ? (
            <p className="text-sm text-slate-500 italic">Loading draft…</p>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Recipient(s) — comma separated</Label>
                <Input
                  data-testid="recall-email-to"
                  value={emailRecipient}
                  onChange={(e) => setEmailRecipient(e.target.value)}
                  placeholder="consignee@example.com"
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subject</Label>
                <Input
                  data-testid="recall-email-subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="rounded-sm"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Body</Label>
                <RichTextEditor
                  testId="recall-email-body"
                  value={emailBody}
                  onChange={setEmailBody}
                  placeholder="Compose the recall letter…"
                  minHeight={280}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailingUnit(null)} disabled={sendingEmail} data-testid="recall-email-cancel">Cancel</Button>
            <Button
              onClick={sendRecallEmail}
              disabled={sendingEmail || !emailRecipient || !emailSubject || !emailBody}
              className="bg-red-600 hover:bg-red-700"
              data-testid="recall-email-send"
            >
              <Send className="w-4 h-4 mr-2" /> {sendingEmail ? 'Sending…' : 'Send Recall Letter'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BatchLotRecallPage;
